import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Key, Calendar, Ban, CheckCircle, Eye } from 'lucide-react';
// import { adminAPI } from '../../services/api';
import './AdminUsers.css';

interface User {
  id: string;
  email: string;
  name: string;
  claimed_keys: string[];
  created_at: string;
  status?: string;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // TODO: Implement admin user list endpoint
      // const response = await adminAPI.listUsers();
      // setUsers(response.data.users);
      
      // Dummy data for now
      setUsers([
        {
          id: '1',
          email: 'user1@example.com',
          name: 'John Doe',
          claimed_keys: ['sk-xxx-1', 'sk-xxx-2'],
          created_at: '2024-01-15T10:30:00Z',
          status: 'active'
        },
        {
          id: '2',
          email: 'user2@example.com',
          name: 'Jane Smith',
          claimed_keys: ['sk-xxx-3'],
          created_at: '2024-02-20T14:20:00Z',
          status: 'active'
        }
      ]);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-users">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>View and manage all registered users</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="search-bar">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search users by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-header">
          <h2>
            <Users size={20} />
            All Users ({filteredUsers.length})
          </h2>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading"></div>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>API Keys</th>
                  <th>Created</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <span className="user-name">{user.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="email-cell">
                        <Mail size={16} />
                        {user.email}
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-info">
                        <Key size={14} />
                        {user.claimed_keys.length} keys
                      </span>
                    </td>
                    <td>
                      <div className="date-cell">
                        <Calendar size={16} />
                        {new Date(user.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <span className={`badge badge-${user.status === 'active' ? 'success' : 'error'}`}>
                        {user.status === 'active' ? <CheckCircle size={14} /> : <Ban size={14} />}
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => setSelectedUser(user)}
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {selectedUser && (
        <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="modal-close" onClick={() => setSelectedUser(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="detail-item">
                  <label>Name</label>
                  <p>{selectedUser.name}</p>
                </div>
                <div className="detail-item">
                  <label>Email</label>
                  <p>{selectedUser.email}</p>
                </div>
                <div className="detail-item">
                  <label>User ID</label>
                  <p className="monospace">{selectedUser.id}</p>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <p>
                    <span className={`badge badge-${selectedUser.status === 'active' ? 'success' : 'error'}`}>
                      {selectedUser.status}
                    </span>
                  </p>
                </div>
                <div className="detail-item">
                  <label>Created At</label>
                  <p>{new Date(selectedUser.created_at).toLocaleString()}</p>
                </div>
                <div className="detail-item">
                  <label>API Keys</label>
                  <div className="keys-list">
                    {selectedUser.claimed_keys.map((key, idx) => (
                      <div key={idx} className="key-item">
                        <Key size={14} />
                        <span className="monospace">{key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setSelectedUser(null)}>
                Close
              </button>
              <button className="btn btn-error">
                <Ban size={16} />
                Suspend User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;